import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, AlertTriangle, Wrench, CheckCircle, Loader, Droplets, Zap, Building, Bug, Thermometer, FileText, Microwave, Flower2, Waves, ArrowLeft, X } from 'lucide-react';
import api from '../api/apiClient';
import { FieldError, useFormErrors } from '../components/ui/FieldError';

interface Property {
  id: number;
  address: string;
  leaseId: number;
}

const CATEGORIES = [
  { value: 'PLUMBING', label: 'Plumbing', icon: Droplets },
  { value: 'ELECTRICAL', label: 'Electrical', icon: Zap },
  { value: 'APPLIANCE', label: 'Appliance', icon: Microwave },
  { value: 'STRUCTURAL', label: 'Structural', icon: Building },
  { value: 'PEST', label: 'Pest Control', icon: Bug },
  { value: 'HVAC', label: 'Heating/Cooling', icon: Thermometer },
  { value: 'GARDENING', label: 'Gardening', icon: Flower2 },
  { value: 'POOL', label: 'Pool Maintenance', icon: Waves },
  { value: 'OTHER', label: 'Other', icon: FileText }
];

const URGENCY_LEVELS = [
  { value: 'LOW',       label: 'Low Priority',  description: 'Can wait a week or more',              sla: '7 days',   tone: 'ok' as const },
  { value: 'MEDIUM',    label: 'Medium',         description: 'Should be fixed within a few days',    sla: '3 days',   tone: 'warn' as const },
  { value: 'HIGH',      label: 'High Priority',  description: 'Needs attention within 24 hours',      sla: '24 hrs',   tone: 'danger' as const },
  { value: 'EMERGENCY', label: 'Emergency',      description: 'Immediate safety hazard or major damage', sla: 'Now',   tone: 'danger' as const },
];

const urgencyClass: Record<string, string> = {
  LOW:       'border-teal-400 bg-teal-50 text-teal-700',
  MEDIUM:    'border-amber-400 bg-amber-50 text-amber-700',
  HIGH:      'border-coral-600 bg-coral-50 text-coral-700',
  EMERGENCY: 'border-red-600 bg-red-50 text-red-700',
};

export default function SubmitMaintenanceRequest() {
  const navigate = useNavigate();

  // Form state
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<number | null>(null);
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState('MEDIUM');
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreviews, setMediaPreviews] = useState<string[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [requestId, setRequestId] = useState<number | null>(null);
  const { errors: fieldErrors, validate } = useFormErrors<{ property: string; category: string; description: string }>();

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const response = await api.get<{leases: any[]}>('/renter/onboarding/available-leases');
      const propertyList = response.leases?.map(lease => ({
        id: lease.propertyId,
        address: lease.propertyAddress,
        leaseId: lease.id
      })) || [];

      setProperties(propertyList);
      if (propertyList.length > 0) {
        setSelectedProperty(propertyList[0].leaseId);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f =>
      f.type.startsWith('image/') || f.type.startsWith('video/')
    );

    if (validFiles.length + mediaFiles.length > 5) {
      alert('Maximum 5 photos/videos allowed');
      return;
    }

    setMediaFiles([...mediaFiles, ...validFiles]);

    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeMedia = (index: number) => {
    setMediaFiles(mediaFiles.filter((_, i) => i !== index));
    setMediaPreviews(mediaPreviews.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const valid = validate({
      property:    () => !selectedProperty ? 'Please select a property' : undefined,
      category:    () => !category ? 'Please select a category' : undefined,
      description: () => description.length < 10 ? 'Please provide at least 10 characters' : undefined,
    });
    if (!valid) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await api.post<{requestId: number}>('/maintenance', {
        leaseId: selectedProperty,
        category,
        description,
        urgency,
        mediaUrls: [],   // file upload requires cloud storage; left for future sprint
      });

      setRequestId(response.requestId);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit maintenance request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-forest-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (success && requestId) {
    return (
      <div className="px-6 py-10 max-w-lg mx-auto">
        <div
          className="rounded-16 p-8 text-white text-center mb-6"
          style={{ background: 'linear-gradient(135deg, var(--forest-900), var(--forest-700))' }}
        >
          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-white" />
          </div>
          <h2 className="text-2xl font-semibold mb-1">Request Submitted!</h2>
          <p className="text-white/70 text-[13px]">Your maintenance request has been received.</p>
        </div>
        <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-6 mb-5 text-center">
          <p className="text-[12px] text-ink-500 uppercase tracking-wider mb-1">Request ID</p>
          <p className="text-3xl font-bold text-forest-900 tabular-nums">#{requestId}</p>
          <p className="text-[13px] text-ink-500 mt-2">You'll receive updates as we work on your request.</p>
        </div>
        <div className="space-y-2">
          <button
            onClick={() => navigate('/renter/dashboard')}
            className="w-full py-3 rounded-10 bg-forest-900 text-white font-semibold text-[14px] hover:bg-forest-800 transition-colors"
          >
            Back to Dashboard
          </button>
          <button
            onClick={() => { setSuccess(false); setRequestId(null); setCategory(''); setDescription(''); setMediaFiles([]); setMediaPreviews([]); }}
            className="w-full py-3 rounded-10 border border-teal-400 text-teal-700 font-semibold text-[14px] hover:bg-teal-50 transition-colors"
          >
            Submit Another Request
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 max-w-2xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-8 h-8 rounded-8 border border-ink-200 flex items-center justify-center text-ink-700 hover:bg-ink-100 transition-colors"
        >
          <ArrowLeft size={16} />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-ink-900">Submit Maintenance Request</h1>
          <p className="text-[13px] text-ink-500">Report an issue that needs repair or attention</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-10 bg-danger-50 border border-red-200 text-[13px] text-danger-700 flex items-start gap-2">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Property Selection */}
        <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5">
          <label className="block text-[13px] font-semibold text-ink-700 mb-2">Property *</label>
          <select
            value={selectedProperty || ''}
            onChange={(e) => setSelectedProperty(Number(e.target.value))}
            className="w-full px-3 py-2.5 border border-ink-200 rounded-10 text-[13px] text-ink-900 focus:outline-none focus:ring-2 focus:ring-forest-900 focus:border-transparent bg-white"
            required
          >
            <option value="">Select a property</option>
            {properties.map((property) => (
              <option key={property.leaseId} value={property.leaseId}>
                {property.address}
              </option>
            ))}
          </select>
          {properties.length === 0 && (
            <p className="text-[12px] text-ink-400 mt-1.5">No active leases found. Contact your agent.</p>
          )}
          <FieldError message={fieldErrors.property} />
        </div>

        {/* Category */}
        <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5">
          <label className="block text-[13px] font-semibold text-ink-700 mb-3">Category *</label>
          <div className="grid grid-cols-3 gap-2">
            {CATEGORIES.map((cat) => {
              const IconComponent = cat.icon;
              return (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`p-3 border-2 rounded-10 text-center transition-all ${
                    category === cat.value
                      ? 'border-forest-900 bg-forest-50'
                      : 'border-ink-200 hover:border-ink-300'
                  }`}
                >
                  <IconComponent
                    size={22}
                    className={`mx-auto mb-1.5 ${category === cat.value ? 'text-forest-900' : 'text-ink-500'}`}
                  />
                  <p className={`text-[11px] font-medium leading-tight ${category === cat.value ? 'text-forest-900' : 'text-ink-700'}`}>
                    {cat.label}
                  </p>
                </button>
              );
            })}
          </div>
          <FieldError message={fieldErrors.category} />
        </div>

        {/* Description */}
        <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5">
          <label className="block text-[13px] font-semibold text-ink-700 mb-2">Description *</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the issue in detail — what happened, where it is, and how long it's been a problem…"
            className="w-full px-3 py-2.5 border border-ink-200 rounded-10 text-[13px] text-ink-900 focus:outline-none focus:ring-2 focus:ring-forest-900 focus:border-transparent resize-none"
            rows={5}
            minLength={10}
            required
          />
          <p className="text-[11px] text-ink-400 mt-1">
            {description.length < 10
              ? `${10 - description.length} more character${10 - description.length !== 1 ? 's' : ''} needed`
              : `${description.length} characters`}
          </p>
          <FieldError message={fieldErrors.description} />
        </div>

        {/* Photos */}
        <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5">
          <label className="block text-[13px] font-semibold text-ink-700 mb-3">
            Photos / Videos <span className="text-ink-400 font-normal">(optional, up to 5)</span>
          </label>
          {mediaPreviews.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-3">
              {mediaPreviews.map((preview, index) => (
                <div key={index} className="relative rounded-8 overflow-hidden aspect-square">
                  <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeMedia(index)}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center"
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {mediaPreviews.length < 5 && (
            <label
              htmlFor="media-upload"
              className="flex flex-col items-center justify-center gap-2 p-5 border-2 border-dashed border-ink-200 rounded-10 cursor-pointer hover:border-forest-900 hover:bg-forest-50 transition-all"
            >
              <Camera size={28} className="text-ink-400" />
              <p className="text-[13px] font-medium text-forest-900">Click to add photos</p>
              <p className="text-[11px] text-ink-400">Images or video files</p>
              <input
                id="media-upload"
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          )}
          <p className="text-[11px] text-ink-400 mt-2">
            Note: Photo upload is stored locally and will be attached when cloud storage is configured.
          </p>
        </div>

        {/* Urgency */}
        <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5">
          <label className="block text-[13px] font-semibold text-ink-700 mb-3">Urgency Level *</label>
          <div className="space-y-2">
            {URGENCY_LEVELS.map((level) => (
              <label
                key={level.value}
                className={`flex items-start gap-3 p-4 border-2 rounded-10 cursor-pointer transition-all ${
                  urgency === level.value
                    ? `border-forest-900 bg-forest-50`
                    : 'border-ink-200 hover:border-ink-300'
                }`}
              >
                <input
                  type="radio"
                  name="urgency"
                  value={level.value}
                  checked={urgency === level.value}
                  onChange={(e) => setUrgency(e.target.value)}
                  className="mt-1 accent-forest-900"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="text-[13px] font-semibold text-ink-900">{level.label}</p>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${urgencyClass[level.value]}`}>
                      {level.sla}
                    </span>
                  </div>
                  <p className="text-[12px] text-ink-500">{level.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting || !selectedProperty || !category || description.length < 10}
          className="w-full py-3 rounded-10 bg-forest-900 text-white font-semibold text-[14px] hover:bg-forest-800 disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader size={18} className="animate-spin" />
              Submitting…
            </>
          ) : (
            <>
              <Wrench size={18} />
              Submit Request
            </>
          )}
        </button>

      </form>
    </div>
  );
}
